import { useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PhoneInput } from '@/components/ui/phone-input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useLocation, Link } from 'wouter';
import { AlertCircle, CheckCircle } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { getDefaultCountry, getDefaultCountryAsync } from '@/utils/country-detection';
import { type CountryCode } from 'libphonenumber-js';
import { MobileAppRedirect } from '@/components/MobileAppRedirect';
import { isMobileDevice } from '@/utils/mobile-detection';
import { API_ROUTES } from '@mangelapp/shared';
import { Checkbox } from '@/components/ui/checkbox';
import { localizedFolwMarketingPageUrl } from '@/utils/folw-marketing-urls';

function buildSchema(t: (k: string, opts?: any) => string) {
  const isStaging = import.meta.env.VITE_APP_ENV === 'staging';
  return z.object({
    email: z.string()
      .trim()
      .min(1, t('pages.register:validation.email.required'))
      .email(t('pages.register:validation.email.invalid')),
    password: z.string()
      .min(6, t('pages.register:validation.password.min'))
      .max(50, t('pages.register:validation.password.max')),
    confirmPassword: z.string()
      .min(1, t('pages.register:validation.confirmPassword.required')),
    firstName: z.string()
      .trim()
      .min(1, t('pages.register:validation.firstName.required'))
      .max(50, t('pages.register:validation.firstName.max')),
    lastName: z.string()
      .trim()
      .min(1, t('pages.register:validation.lastName.required'))
      .max(50, t('pages.register:validation.lastName.max')),
    phone: z.string()
      .optional()
      .or(z.literal(''))
      .refine((val) => {
        // If phone is provided, validate it
        if (!val || val.trim() === '') return true;
        return isValidPhoneNumber(val);
      }, {
        message: t('pages.register:validation.phone.invalid'),
      }),
    company: z.string()
      .trim()
      .min(1, t('pages.register:validation.company.required'))
      .max(100, t('pages.register:validation.company.max')),
    registrationKey: z.string().optional(),
    acceptLegal: z.boolean().refine((v) => v === true, {
      message: t('pages.register:legal.required'),
    }),
  }).superRefine((data, ctx) => {
    if (isStaging) {
      const key = (data.registrationKey || '').trim();
      if (!key) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('pages.register:validation.registrationKey.required'),
          path: ['registrationKey'],
        });
      }
    }
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('pages.register:validation.passwordsMismatch'),
    path: ['confirmPassword'],
  });
}

type RegisterFormData = z.infer<ReturnType<typeof buildSchema>>;

export function RegisterPage() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [registeredUser, setRegisteredUser] = useState<{userCode: string, username: string} | null>(null);
  const [defaultCountry, setDefaultCountry] = useState<CountryCode>(getDefaultCountry());
  const [, setLocation] = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Check if user is on mobile device
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  // Detect country with IP geolocation when page loads
  useEffect(() => {
    getDefaultCountryAsync().then((detectedCountry) => {
      if (detectedCountry) {
        setDefaultCountry(detectedCountry);
      }
    }).catch(() => {
      // Silently fail - we already have a fallback from getDefaultCountry()
    });
  }, []);

  const schema = useMemo(() => buildSchema(t), [i18n.language]);
  const form = useForm<RegisterFormData>({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      company: '',
      registrationKey: '',
      acceptLegal: false,
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ROUTES.AUTH.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language || 'en',
        },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          confirmPassword: data.confirmPassword,
          firstName: data.firstName,
          lastName: data.lastName,
          displayName: `${data.firstName} ${data.lastName}`,
          phone: data.phone || null,
          company: data.company?.trim() || '',
          registrationKey: data.registrationKey?.trim() || undefined,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setRegisteredUser({
          userCode: result.userCode,
          username: data.email // Use email as username
        });
        setShowSuccessDialog(true);
        form.reset();
      } else {
        const error = await response.json();
        setErrorMessage(error.message || t('pages.register:error.generic'));
        setShowErrorDialog(true);
      }
    } catch (error) {
      setErrorMessage(t('pages.register:error.network'));
      setShowErrorDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Show mobile app redirect if on mobile device
  if (isMobile) {
    return <MobileAppRedirect />;
  }

  return (
    <div className="auth-page">
      <Card className="w-full max-w-2xl relative card-container-muted card-container-no-shadow card-container-border-black">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher compact />
        </div>
        <CardHeader className="text-center pb-6 pt-8">
          <div className="flex flex-col items-center justify-center mb-4">
            <img 
              src="/images/icons/SquareLogo.svg" 
              alt="FOLW Logo" 
              className="h-16 w-16 mb-3"
            />
            <img 
              src="/images/logos/FolwLogoText.svg" 
              alt="FOLW" 
              className="h-8"
            />
          </div>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <Form {...form}>
            <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const values = form.getValues();
              const result = schema.safeParse(values);

              if (!result.success) {
                form.clearErrors();
                for (const issue of result.error.issues) {
                  const field = issue.path?.[0] as keyof RegisterFormData;
                  if (field) {
                    form.setError(field, {
                      type: 'validation',
                      message: issue.message,
                    }, { shouldFocus: false });
                  }
                }
                return;
              }

              void onSubmit(result.data);
            }} className="space-y-4">
              {/* Benutzerdaten */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-form-label mb-1.5 block">
                        {t('pages.register:fields.firstName')} *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder={t('pages.register:placeholders.firstName')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-form-label mb-1.5 block">
                        {t('pages.register:fields.lastName')} *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder={t('pages.register:placeholders.lastName')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Anmeldedaten */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-form-label mb-1.5 block">
                      {t('pages.register:fields.email')} *
                    </FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="email"
                        placeholder={t('pages.register:placeholders.email')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {import.meta.env.VITE_APP_ENV === 'staging' && (
                <FormField
                  control={form.control}
                  name="registrationKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-form-label mb-1.5 block">
                        {t('pages.register:fields.registrationKey')} *
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('pages.register:placeholders.registrationKey')}
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Passwort */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-form-label mb-1.5 block">
                        {t('pages.register:fields.password')} *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          type="password"
                          placeholder={t('pages.register:placeholders.password')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-form-label mb-1.5 block">
                        {t('pages.register:fields.confirmPassword')} *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          type="password"
                          placeholder={t('pages.register:placeholders.password')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Phone (optional) and Company (required) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-form-label mb-1.5 block">
                        {t('pages.register:fields.phoneOptional')}
                      </FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value || ''}
                          onChange={(value) => field.onChange(value)}
                          onBlur={field.onBlur}
                          error={form.formState.errors.phone?.message}
                          defaultCountry={defaultCountry}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-form-label mb-1.5 block">
                        {t('pages.register:fields.company')} *
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder={t('pages.register:placeholders.company')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="acceptLegal"
                render={({ field }) => (
                  <FormItem className="space-y-0 pt-2">
                    <div className="flex flex-row items-start gap-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(c) => field.onChange(c === true)}
                          disabled={isLoading}
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-primary data-[state=checked]:bg-[var(--primary-blue)] data-[state=checked]:border-primary"
                        />
                      </FormControl>
                      <div className="text-xs leading-relaxed text-form-label">
                        <span>{t('pages.register:legal.lead')}</span>
                        <a
                          href={localizedFolwMarketingPageUrl(i18n.language, 'privacypolicy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-blue underline hover:text-[#3a7bc8] font-medium"
                        >
                          {t('pages.register:legal.privacyLink')}
                        </a>
                        <span>{t('pages.register:legal.middle')}</span>
                        <a
                          href={localizedFolwMarketingPageUrl(i18n.language, 'generalterms')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-blue underline hover:text-[#3a7bc8] font-medium"
                        >
                          {t('pages.register:legal.termsLink')}
                        </a>
                        <span>{t('pages.register:legal.suffix')}</span>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={isLoading || !form.watch('acceptLegal')}
                  className="w-full"
                >
                  {isLoading ? t('pages.register:buttons.submitting') : t('pages.register:buttons.submit')}
                </Button>
                
                <div className="text-center mt-2">
                  <Link href="/login" className="text-primary-blue hover:text-[#3a7bc8] text-xs font-medium">
                    {t('pages.register:loginNow')}
                  </Link>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md card-container card-container-muted card-container-no-shadow card-container-border-black">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              {t('pages.register:success.title')}
            </DialogTitle>
            <div className="mt-3 space-y-3">
              <div className="text-sm text-gray-600">
                {t('pages.register:success.line1')}
              </div>
              {registeredUser && (
                <div className="bg-blue-50 p-3 rounded-md">
                  <div className="text-xs font-medium text-blue-800 mb-1">{t('pages.register:success.credentialsTitle')}</div>
                  <div className="text-xs text-blue-700">
                    <div><strong>{t('pages.register:success.usernameLabel')}</strong> {registeredUser.username}</div>
                    <div><strong>{t('pages.register:success.userCodeLabel')}</strong> {registeredUser.userCode}</div>
                  </div>
                </div>
              )}
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="text-sm text-yellow-800 font-medium mb-1">
                  {t('pages.register:verification.sent')}
                </div>
                <div className="text-xs text-yellow-700">
                  {t('pages.register:verification.checkEmail')}
                </div>
              </div>
              <div className="text-sm text-gray-600">
                {t('pages.register:success.line2')}
              </div>
            </div>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                setLocation('/login');
              }}
              size="sm"
            >
              {t('pages.register:success.toLogin')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-md card-container card-container-muted card-container-no-shadow card-container-border-black">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              {t('pages.register:error.title')}
            </DialogTitle>
            <div className="mt-3">
              <div className="text-sm text-gray-600">
                {errorMessage}
              </div>
            </div>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              onClick={() => setShowErrorDialog(false)}
              variant="outline"
              size="sm"
            >
              {t('pages.register:error.ok')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
